import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { 
  Globe, 
  Smartphone, 
  TestTube2, 
  Eye, 
  Monitor,
  ArrowRight,
  CheckCircle2,
  Zap
} from 'lucide-react';
import PageLayout, { PageHero } from '../layout/PageLayout';
import SEO from '../SEO';

const SolutionsIndex = ({ onBack, onLogin, onRegister, navigateTo }) => {
  const solutions = [
    {
      title: 'Web Testing',
      description: 'AI-powered browser automation for modern web applications. Test across all browsers with Playwright.',
      icon: Globe,
      path: '/solutions/web-testing',
      gradient: 'from-blue-500 to-cyan-500',
      features: ['Cross-browser testing', 'AI-generated selectors', 'Visual validation']
    },
    {
      title: 'Mobile Testing',
      description: 'Comprehensive mobile app testing for iOS and Android. Native and hybrid app support.',
      icon: Smartphone,
      path: '/solutions/mobile-testing',
      gradient: 'from-green-500 to-emerald-500',
      features: ['iOS & Android', 'Device emulation', 'Gesture testing']
    },
    {
      title: 'API Testing',
      description: 'Automate REST, GraphQL, and SOAP API testing with intelligent assertions.',
      icon: TestTube2,
      path: '/solutions/api-testing',
      gradient: 'from-orange-500 to-amber-500',
      features: ['REST & GraphQL', 'Auto-generated tests', 'Response validation']
    },
    {
      title: 'Visual Testing',
      description: 'Catch visual regressions automatically. Pixel-perfect comparisons with AI-powered diff detection.',
      icon: Eye,
      path: '/solutions/visual-testing',
      gradient: 'from-purple-500 to-pink-500',
      features: ['Pixel comparison', 'AI diff detection', 'Baseline management']
    },
    {
      title: 'Cross-Browser Testing',
      description: 'Ensure consistency across Chrome, Firefox, Safari, and Edge with parallel execution.',
      icon: Monitor,
      path: '/solutions/cross-browser',
      gradient: 'from-indigo-500 to-violet-500',
      features: ['All major browsers', 'Parallel execution', 'Screenshot comparison']
    }
  ];

  const handleNavigate = (path) => {
    if (navigateTo) {
      navigateTo(path);
    } else {
      window.history.pushState(null, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  return (
    <PageLayout onBack={onBack} onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Testing Solutions - Web, Mobile, API & Visual Testing | Lexa.run"
        description="Explore Lexa.run's comprehensive testing solutions. AI-powered web testing, mobile testing, API testing, visual testing, and cross-browser testing."
      />

      {/* Hero Section */}
      <PageHero
        badge={
          <>
            <Zap className="w-4 h-4" />
            <span>AI-Powered Testing Solutions</span>
          </>
        }
        title="Complete Testing"
        titleHighlight="Solutions"
        subtitle="From web and mobile testing to API validation and visual regression detection. Our AI-powered platform covers every aspect of your testing needs."
      />

      {/* Solutions Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Card 
                key={index}
                className="group relative overflow-hidden p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-gray-200/50"
                onClick={() => handleNavigate(solution.path)}
                data-testid={`solution-card-${solution.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity bg-gradient-to-br ${solution.gradient}`} />
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${solution.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                    <solution.icon className="w-7 h-7 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors">
                    {solution.title}
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {solution.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {solution.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center text-purple-600 font-medium group-hover:gap-3 gap-2 transition-all">
                    Learn more
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to transform your testing?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Start with 5 free AI-generated tests. No credit card required.
          </p>
          <Button 
            onClick={onRegister}
            size="lg"
            className="rounded-full bg-white text-purple-600 hover:bg-gray-100 px-10 py-6 text-lg font-semibold shadow-xl"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </PageLayout>
  );
};

export default SolutionsIndex;
